import { client } from "../api/client.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { confirmModal } from "../ui/confirmModal.js";
import { toast } from "../ui/toast.js";
import { MCP_PUBLIC_URL } from "../data/mcpPrompts.js";

function isProTier() {
    const u = window.__auth__ || {};
    if (['admin', 'angel_admin', 'god_admin'].includes(u.role)) return true;
    // Mirror server/middleware/tier.js MCP_ALLOWED_TIERS — trial users
    // get full Pro access during their evaluation window.
    return ['trial', 'pro', 'business', 'founder', 'agency'].includes(u.tier);
}

/**
 * Connectors page — manage API keys for external LLM clients
 * (Claude Desktop, Cursor, ChatGPT, Copilot, etc.) via the heist-mcp
 * stdio server or direct REST calls.
 */
export class ConnectorsPage {
    constructor(opts = {}) {
        this.state = {
            loading: true,
            error: null,
            keys: [],
            availableScopes: [],
            creating: false,
            showRevoked: false, // toggle for revealing revoked keys in the table
            advancedOpen: false, // whole "Advanced setup" section collapsed by default
            newlyCreatedKey: null, // { key, keyPrefix, name } — shown once after create in a modal
            draft: {
                name: '',
                scopes: new Set(['read', 'draft']),
                brandIds: [],
            },
        };
        this.el = null;
        // When `embedded` is true the page-level header is suppressed so the
        // connector content can sit inside another container that already has
        // its own header — e.g. the Settings → Connectors tab.
        this.embedded = !!opts.embedded;
    }

    async mount(el) {
        this.el = el;
        // Read deep-link query (?platform=…&advanced=1) BEFORE first render so
        // the Advanced section is open on initial paint when needed.
        const q = (window.location.hash || '').split('?')[1] || '';
        const params = new URLSearchParams(q);
        this._deepLinkPlatform = params.get('platform') || null;
        if (params.get('advanced') === '1') this.state.advancedOpen = true;
        this.render();
        this.attachListeners();
        await this.load();
        // Once content is rendered, scroll the relevant card into view based on
        // the deep-link platform. Browser-OAuth platforms (claude/chatgpt) get
        // the primary card; desktop-app platforms (cursor/windsurf) get the
        // Advanced section that's already auto-expanded above.
        this.scrollDeepLinkIntoView();
        // Poll /me every 10s while the page is open so the "Connected"
        // pill flips the moment the user finishes the connector setup
        // inside Claude.ai (or any other MCP client).
        this._pollMcp = setInterval(async () => {
            try {
                const me = await client.authMe();
                const wasConnected = !!(window.__auth__ && window.__auth__.mcp_connected);
                if (window.__auth__ && me.user) {
                    window.__auth__.mcp_connected = !!me.user.mcp_connected;
                    window.__auth__.mcp_connected_at = me.user.mcp_connected_at || null;
                }
                if (!wasConnected && window.__auth__?.mcp_connected) {
                    this.render();
                    try { window.dispatchEvent(new CustomEvent('heist:mcp-connected')); } catch (_) {}
                }
            } catch (_) {}
        }, 10000);
    }

    async unmount() {
        if (this._pollMcp) { clearInterval(this._pollMcp); this._pollMcp = null; }
    }

    async load() {
        try {
            const res = await client.listApiKeys();
            this.setState({
                loading: false,
                keys: res.keys || [],
                availableScopes: res.availableScopes || [],
            });
        } catch (err) {
            this.setState({ loading: false, error: err.message });
        }
    }

    setState(patch) {
        Object.assign(this.state, patch);
        this.render();
    }

    /**
     * Smooth-scroll the right card into view based on the deep-link
     * platform query (?platform=claude|chatgpt|cursor|windsurf). Safe
     * to call before or after async content has loaded — no-op if the
     * target element isn't in the DOM yet.
     */
    scrollDeepLinkIntoView() {
        if (!this._deepLinkPlatform) return;
        const desktopApps = new Set(['cursor', 'windsurf', 'copilot']);
        // Defer one frame so the latest render has flushed to the DOM.
        requestAnimationFrame(() => {
            const target = desktopApps.has(this._deepLinkPlatform)
                ? this.el.querySelector('.conn-advanced')
                : this.el.querySelector('.conn-card--primary');
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }

    attachListeners() {
        this.el.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;
            const action = btn.dataset.action;
            if (action === 'toggle-scope') {
                const scope = btn.dataset.scope;
                if (this.state.draft.scopes.has(scope)) this.state.draft.scopes.delete(scope);
                else this.state.draft.scopes.add(scope);
                this.render();
            } else if (action === 'create') {
                await this.createKey();
            } else if (action === 'dismiss-new-key') {
                this.setState({ newlyCreatedKey: null, draft: { name: '', scopes: new Set(['read', 'draft']), brandIds: [] } });
            } else if (action === 'toggle-revoked') {
                this.setState({ showRevoked: !this.state.showRevoked });
            } else if (action === 'toggle-advanced') {
                this.setState({ advancedOpen: !this.state.advancedOpen });
            } else if (action === 'copy-key') {
                await this._copy(this.state.newlyCreatedKey.key, 'API key copied to clipboard', btn);
            } else if (action === 'copy-key-and-close') {
                const key = this.state.newlyCreatedKey?.key;
                if (key) {
                    try {
                        await navigator.clipboard.writeText(key);
                        toast('success','API key copied to clipboard');
                        this._flashCopied(btn);
                    } catch (_) {
                        toast('error','Could not copy — please use the Copy key button');
                        return;
                    }
                }
                // Let the flash be visible for a beat before dismissing
                setTimeout(() => {
                    this.setState({ newlyCreatedKey: null, draft: { name: '', scopes: new Set(['read', 'draft']), brandIds: [] } });
                }, 700);
            } else if (action === 'copy-text') {
                await this._copy(btn.dataset.copy, btn.dataset.label || 'Copied', btn);
            } else if (action === 'revoke') {
                const id = btn.dataset.id;
                const name = btn.dataset.name;
                const ok = await confirmModal({
                    title: 'Revoke API key?',
                    message: `The key "${name}" will stop working immediately. Any connected tool (Claude Desktop, Cursor, etc.) will lose access.`,
                    confirmLabel: 'Revoke',
                    danger: true,
                });
                if (ok) await this.revoke(id);
            }
        });
        this.el.addEventListener('input', (e) => {
            if (e.target.id === 'key-name') {
                this.state.draft.name = e.target.value;
            }
        });
    }

    async _copy(text, msg, btn) {
        try {
            await navigator.clipboard.writeText(text);
            toast('success',msg || 'Copied');
            if (btn) this._flashCopied(btn);
        } catch (_) {
            toast('error','Could not copy — select and copy manually');
        }
    }

    _flashCopied(btn) {
        // Swap button label to "✓ Copied" for 1.4s with a success tint,
        // plus a floating "Copied!" pill above the button for extra visibility.
        if (btn.dataset.copyFlashing === '1') return;
        btn.dataset.copyFlashing = '1';
        btn.dataset.originalHtml = btn.innerHTML;
        btn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:5px"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</span>';
        btn.classList.add('is-copied');

        // Floating pill
        try {
            if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
            const pill = document.createElement('span');
            pill.className = 'conn-copied-pill';
            pill.textContent = 'Copied!';
            btn.appendChild(pill);
            setTimeout(() => { pill.remove(); }, 1400);
        } catch (_) {}

        setTimeout(() => {
            btn.innerHTML = btn.dataset.originalHtml || '';
            btn.classList.remove('is-copied');
            delete btn.dataset.copyFlashing;
            delete btn.dataset.originalHtml;
        }, 1400);
    }

    async createKey() {
        const name = (this.state.draft.name || '').trim();
        if (!name) { toast('error','Give the key a name first'); return; }
        const scopes = Array.from(this.state.draft.scopes);
        if (scopes.length === 0) { toast('error','Select at least one scope'); return; }
        this.setState({ creating: true });
        try {
            const res = await client.createApiKey({ name, scopes });
            this.setState({
                creating: false,
                newlyCreatedKey: { key: res.key, keyPrefix: res.keyPrefix, name: res.name, scopes: res.scopes },
            });
            await this.load();
        } catch (err) {
            this.setState({ creating: false });
            toast('error',err.message || 'Failed to create key');
        }
    }

    async revoke(id) {
        try {
            await client.revokeApiKey(id);
            toast('success','Key revoked');
            await this.load();
        } catch (err) {
            toast('error',err.message || 'Failed to revoke');
        }
    }

    render() {
        if (!this.el) return;
        const s = this.state;
        // Skip the page-header when embedded inside another shell (Settings tab).
        const headerHtml = this.embedded ? '' : renderPageHeader({
            title: 'Connectors',
            subtitle: 'Write posts in Claude, ChatGPT, Cursor, or Copilot — and have them show up in Heist for you to review and publish.',
            tourPlaylist: 'connectors',
        });

        if (!isProTier()) {
            this.el.innerHTML = headerHtml + renderUpgradeWall();
            return;
        }

        const scopeOptions = (s.availableScopes.length ? s.availableScopes : ['read', 'draft', 'schedule', 'publish', 'auto_approve', 'memory:write']);
        const scopeMeta = {
            'read': { dot: '#10B981', desc: 'Let the AI read your brand info and past posts so its drafts sound like you.' },
            'draft': { dot: '#8B5CF6', desc: 'Let the AI write new post drafts. They land in your Agent Inbox for you to approve.' },
            'schedule': { dot: '#3B82F6', desc: 'Let the AI schedule posts you\'ve already approved.' },
            'publish': { dot: '#EF4444', desc: 'Let the AI post directly to your social accounts. Only turn this on if you fully trust it.' },
            'auto_approve': { dot: '#F59E0B', desc: 'Skip the inbox and send drafts straight to your library. Handy once you trust the AI.' },
            'memory:write': { dot: '#06B6D4', desc: 'Let the AI add notes to your brand knowledge as it learns.' },
        };
        const scopeChips = scopeOptions.map(scope => {
            const selected = s.draft.scopes.has(scope);
            const m = scopeMeta[scope] || { dot: '#64748B', desc: '' };
            return `<button type="button" class="conn-scope ${selected ? 'is-selected' : ''}" data-action="toggle-scope" data-scope="${scope}" title="${escapeHtml(m.desc)}">
                <span class="conn-scope__dot" style="--dot:${m.dot}"></span>
                <span class="conn-scope__label">${scope}</span>
            </button>`;
        }).join('');

        // Always show the production URL — the only address external AI
        // chats (Claude.ai, ChatGPT, Cursor, Windsurf) can actually reach.
        // Single source: MCP_PUBLIC_URL in app/src/data/mcpPrompts.js.
        const remoteMcpUrl = MCP_PUBLIC_URL;
        const tarballUrl = `${window.location.origin}/heist-mcp.tgz`;
        const stdioCmd = `npm install -g ${tarballUrl}`;
        // Optional starter prompt — paste into the AI chat after connecting,
        // and the AI walks the user through filling in their brand profile
        // via the Heist MCP tools. Designed to be flexible: invites
        // "I don't know" answers, website-scan, and document upload, and
        // confirms each piece before saving.
        const BRAND_FILL_PROMPT = `Help me set up my brand profile in Heist. Ask me one question at a time about my mission, voice, audience, goals, and offers. If I don't know an answer, suggest options based on what we've discussed so far — don't push me to commit. I can also share my website URL for you to scan, or upload brand documents (PDFs, decks, brand guides) for you to read and save to Heist. Confirm with me before saving each piece, and let me converse about anything before it gets uploaded.`;
        const mcpConfigSnippet = `{
  "mcpServers": {
    "heist": {
      "command": "heist-mcp",
      "env": {
        "HEIST_URL": "${window.location.origin}",
        "HEIST_API_KEY": "hst_live_..."
      }
    }
  }
}`;
        // VS Code Copilot uses a slightly different schema — `servers`
        // (not `mcpServers`) with an explicit `type` field. Lives in
        // .vscode/mcp.json or in user settings.
        const copilotConfigSnippet = `{
  "servers": {
    "heist": {
      "type": "stdio",
      "command": "heist-mcp",
      "env": {
        "HEIST_URL": "${window.location.origin}",
        "HEIST_API_KEY": "hst_live_..."
      }
    }
  }
}`;

        const activeKeys = s.keys.filter(k => k.active);
        const revokedKeys = s.keys.filter(k => !k.active);
        const visibleKeys = s.showRevoked ? s.keys : activeKeys;
        const revokedToggleHtml = revokedKeys.length > 0
            ? `<button type="button" class="conn-revoked-toggle" data-action="toggle-revoked" aria-expanded="${s.showRevoked ? 'true' : 'false'}">
                 <span class="conn-revoked-toggle__chev">${s.showRevoked ? '▾' : '▸'}</span>
                 ${s.showRevoked ? 'Hide' : 'Show'} revoked
                 <span class="conn-revoked-toggle__count">${revokedKeys.length}</span>
               </button>`
            : '';

        const keysTableHtml = s.loading
            ? `<div class="conn-skeleton"></div><div class="conn-skeleton"></div>`
            : s.error
                ? `<div class="conn-empty conn-empty--error">${escapeHtml(s.error)}</div>`
                : (activeKeys.length === 0 && revokedKeys.length === 0
                    ? `<div class="conn-empty">
                          <div class="conn-empty__title">No API keys yet</div>
                          <div class="conn-empty__sub">Create one below to connect Claude Desktop, Cursor, Windsurf, GitHub Copilot in VS Code, or any custom agent.</div>
                       </div>`
                    : (visibleKeys.length === 0
                        ? `<div class="conn-empty">
                              <div class="conn-empty__title">No active keys</div>
                              <div class="conn-empty__sub">All keys for this workspace have been revoked. Create a new one below, or reveal revoked keys with the toggle.</div>
                           </div>
                           ${revokedToggleHtml ? `<div class="conn-revoked-bar">${revokedToggleHtml}</div>` : ''}`
                        : `<div class="conn-table-wrap"><table class="conn-table"><thead><tr>
                            <th>Name</th><th>Prefix</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Status</th><th></th>
                          </tr></thead><tbody>${visibleKeys.map(k => `
                            <tr class="${k.active ? '' : 'is-revoked'}">
                              <td><div class="conn-table__name">${escapeHtml(k.name)}</div>${k.clientLabel ? `<div class="conn-table__sub">${escapeHtml(k.clientLabel)}</div>` : ''}</td>
                              <td><code class="conn-code-inline">${escapeHtml(k.keyPrefix)}…</code></td>
                              <td>${(k.scopes || []).map(sc => {
                                    const m = scopeMeta[sc] || { dot: '#64748B' };
                                    return `<span class="conn-scope conn-scope--mini"><span class="conn-scope__dot" style="--dot:${m.dot}"></span>${sc}</span>`;
                                  }).join('')}</td>
                              <td class="conn-dim">${k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}</td>
                              <td class="conn-dim">${k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : '—'}</td>
                              <td>${k.active ? '<span class="conn-status conn-status--active">● active</span>' : '<span class="conn-status conn-status--revoked">● revoked</span>'}</td>
                              <td>${k.active ? `<button class="conn-btn conn-btn--danger-ghost" data-action="revoke" data-id="${escapeHtml(k.id)}" data-name="${escapeHtml(k.name)}">Revoke</button>` : ''}</td>
                            </tr>`).join('')}</tbody></table></div>
                          ${revokedToggleHtml ? `<div class="conn-revoked-bar">${revokedToggleHtml}</div>` : ''}`));

        const newKeyModal = s.newlyCreatedKey ? `
            <div class="conn-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="conn-modal-title">
              <div class="conn-modal" role="document">
                <div class="conn-modal__head">
                  <div class="conn-modal__icon">🔑</div>
                  <div>
                    <div id="conn-modal-title" class="conn-modal__title">Your new API key</div>
                    <div class="conn-modal__sub">Copy this now — for security, the full key is shown <strong>only once</strong>. If you lose it, revoke and create a new one.</div>
                  </div>
                </div>
                <div class="conn-modal__keywrap">
                  <div class="conn-modal__keylabel">Full key</div>
                  <div class="conn-modal__keyrow">
                    <code class="conn-code-block">${escapeHtml(s.newlyCreatedKey.key)}</code>
                    <button class="conn-btn conn-btn--copy" data-action="copy-key" aria-label="Copy API key to clipboard">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      Copy key
                    </button>
                  </div>
                </div>
                <div class="conn-modal__meta">
                  <div class="conn-modal__meta-row"><span class="conn-modal__meta-label">Name</span><span>${escapeHtml(s.newlyCreatedKey.name)}</span></div>
                  <div class="conn-modal__meta-row"><span class="conn-modal__meta-label">Scopes</span><span class="conn-modal__scopes">${(s.newlyCreatedKey.scopes || []).map(sc => `<span class="conn-scope conn-scope--mini"><span class="conn-scope__dot" style="--dot:${(scopeMeta[sc]||{}).dot||'#64748B'}"></span>${sc}</span>`).join('')}</span></div>
                </div>

                <div class="conn-modal__where">
                  <div class="conn-modal__where-title">What do I do with this key?</div>
                  <p class="conn-modal__where-intro">This key is <strong>only needed if you're setting up a desktop app</strong> (Claude Desktop, Cursor, or Windsurf). Claude.ai in your browser does <em>not</em> need a key — it uses a sign-in flow instead.</p>
                  <ol class="conn-modal__where-steps">
                    <li><strong>For Claude Desktop / Cursor / Windsurf:</strong> on the Connectors page, open the <em>"Claude Desktop, Cursor, or Windsurf"</em> card, hit <strong>Copy</strong> on the Step 2 config snippet, and paste it into <code>claude_desktop_config.json</code> (or your app's equivalent). Replace the placeholder <code>hst_live_…</code> with the key above. Save, restart the app.</li>
                    <li><strong>For Claude.ai or ChatGPT in the browser:</strong> you don't need this key at all — close this window, follow the top card on the Connectors page instead. You'll sign in with your Heist account when prompted.</li>
                    <li><strong>Not sure?</strong> Copy the key to a safe place (a password manager is ideal) and come back when you're ready. If you lose it, just revoke it and make a new one.</li>
                  </ol>
                </div>

                <div class="conn-modal__actions">
                  <button class="conn-btn conn-btn--ghost" data-action="dismiss-new-key">Close</button>
                  <button class="conn-btn conn-btn--primary conn-btn--lg" data-action="copy-key-and-close">Copy &amp; close</button>
                </div>
              </div>
            </div>
        ` : '';

        this.el.innerHTML = `
          ${headerHtml}
          ${newKeyModal}
          <div class="conn-page">


            <div class="conn-hero">
              <div class="conn-hero__halo" aria-hidden="true"></div>
              <div class="conn-hero__inner">
                <span class="conn-hero__eyebrow">New</span>
                <h2 class="conn-hero__title">Write posts in your favorite AI chat. Review them in Heist.</h2>
                <p class="conn-hero__lede">Work in Claude, ChatGPT, Cursor, or Copilot the way you normally do — ask for post ideas, captions, campaign plans. Every draft the AI writes shows up in your <a href="#agent-inbox">Agent Inbox</a>, ready for you to edit, approve, and publish. Heist teaches the AI about your brand so posts sound like you, not generic marketing copy.</p>
                ${(window.__auth__ && window.__auth__.mcp_connected)
                  ? `<div class="conn-status-pill conn-status-pill--ok" data-testid="mcp-connected-pill">✓ Connected — your AI chat has talked to Heist</div>`
                  : `<div class="conn-status-pill conn-status-pill--idle" data-testid="mcp-not-connected-pill">Not connected yet — follow the steps below. We'll detect it the moment your AI chat hits Heist.</div>`}
              </div>
            </div>

            <section class="conn-section">
              <div class="conn-section__head">
                <h2>Connect your AI chat</h2>
                <p class="conn-dim">Takes about a minute. You'll fill in a short form inside Claude.ai (or ChatGPT) — no downloads, no API keys, no settings files.</p>
              </div>

              <article class="conn-card conn-card--primary">
                <div class="conn-card__rail" style="--rail:linear-gradient(180deg,#D97706,#F59E0B)"></div>
                <header class="conn-card__head">
                  <div class="conn-card__badge">Claude.ai &middot; ChatGPT &middot; Browser</div>
                  <h3>Add Heist as a custom connector</h3>
                  <p>This is the path almost everyone should use. Works on Claude.ai (Pro, Team, Max) and ChatGPT (Pro, Business).</p>
                </header>
                ${(window.__auth__ && window.__auth__.email) ? `
                  <div class="conn-preflight" data-testid="conn-preflight">
                    <div class="conn-preflight__icon">✓</div>
                    <div class="conn-preflight__body">
                      <strong>You're signed in to Heist as ${escapeHtml(window.__auth__.email)}.</strong>
                      <span class="conn-dim">Keep this tab open while you connect — the popup uses this session.</span>
                    </div>
                  </div>
                ` : `
                  <div class="conn-preflight conn-preflight--warn" data-testid="conn-preflight-warn">
                    <div class="conn-preflight__icon">!</div>
                    <div class="conn-preflight__body">
                      <strong>You're not signed in to Heist.</strong>
                      <span class="conn-dim">Sign in first so the popup from your AI chat finds your session. <a href="/login" style="color:var(--primary);">Open the sign-in page →</a></span>
                    </div>
                  </div>
                `}

                <div class="conn-walkthrough">
                  <div class="conn-walkthrough__title">Step 1 — open the Connectors dialog</div>
                  <div class="conn-platform-paths">
                    <div class="conn-platform-path">
                      <div class="conn-platform-path__head"><span class="conn-platform-path__chip" style="--chip:#D97706">Claude.ai</span><span class="conn-platform-path__sub">Pro / Team / Max</span></div>
                      <p class="conn-walkthrough__text">Click your <strong>profile picture</strong> (top-right) → <em>Settings</em> → <em>Connectors</em> → <strong>Add custom connector</strong>. A dialog opens.</p>
                    </div>
                    <div class="conn-platform-path">
                      <div class="conn-platform-path__head"><span class="conn-platform-path__chip" style="--chip:#10A37F">ChatGPT</span><span class="conn-platform-path__sub">Pro / Business with Connectors enabled</span></div>
                      <p class="conn-walkthrough__text">Click your <strong>profile name</strong> (bottom-left) → <em>Settings</em> → <em>Connectors</em> → <strong>Add</strong>. If you don't see a Connectors section your workspace doesn't have custom connectors enabled yet — switch to Claude.ai or use the Advanced setup below.</p>
                    </div>
                  </div>

                  <div class="conn-walkthrough__title">Step 2 — fill in these two fields exactly</div>
                  <p class="conn-walkthrough__text">The dialog asks for a Name and a URL. Paste these values in:</p>

                  <div class="conn-fake-field">
                    <div class="conn-fake-field__label">Name</div>
                    <div class="conn-fake-field__value">
                      <code>Heist</code>
                      <button class="conn-btn conn-btn--ghost conn-btn--sm" data-action="copy-text" data-copy="Heist" data-label="Name copied">Copy</button>
                    </div>
                  </div>
                  <div class="conn-fake-field">
                    <div class="conn-fake-field__label">URL <span class="conn-fake-field__must">must be exactly this</span></div>
                    <div class="conn-fake-field__value">
                      <code>${escapeHtml(remoteMcpUrl)}</code>
                      <button class="conn-btn conn-btn--copy conn-btn--sm" data-action="copy-text" data-copy="${escapeHtml(remoteMcpUrl)}" data-label="Heist URL copied">Copy URL</button>
                    </div>
                  </div>

                  <div class="conn-callout conn-callout--warn">
                    <div class="conn-callout__icon">⚠</div>
                    <div>
                      <strong>Don't touch "Advanced settings."</strong> Leave OAuth Client ID and Client Secret blank — those are for developers, Heist doesn't use them. If you already typed something there, clear both fields before hitting Add.
                    </div>
                  </div>

                  <div class="conn-walkthrough__title">Step 3 — click Add, then sign in to Heist</div>
                  <p class="conn-walkthrough__text">A small Heist sign-in window pops up from your AI chat. <strong>If you don't see it, your browser blocked the popup</strong> — look for a popup-blocked icon in the address bar and allow popups from claude.ai (or chatgpt.com). Sign in with the same Heist account you're using right now, then pick which brands this chat can work with.</p>

                  <div class="conn-walkthrough__title">Step 4 — kickstart your Brain <span class="conn-fake-field__must" style="background:rgba(139,92,246,0.18);color:var(--primary,#A78BFA);">recommended</span></div>
                  <p class="conn-walkthrough__text">Once Heist is connected, paste the prompt below into your AI chat. It walks you through filling in your brand profile (mission, voice, audience, goals) — answer "I don't know" any time and the AI will help you figure it out, scan your website, or read brand documents you upload.</p>

                  <div class="conn-fake-field conn-fake-field--prompt">
                    <div class="conn-fake-field__label">Starter prompt <span class="conn-fake-field__must" style="background:rgba(139,92,246,0.18);color:var(--primary,#A78BFA);">paste in your AI chat</span></div>
                    <div class="conn-fake-field__value conn-fake-field__value--block">
                      <code>${escapeHtml(BRAND_FILL_PROMPT)}</code>
                      <button class="conn-btn conn-btn--copy conn-btn--sm" data-action="copy-text" data-copy="${escapeHtml(BRAND_FILL_PROMPT)}" data-label="Starter prompt copied">Copy prompt</button>
                    </div>
                  </div>

                  <details class="conn-callout conn-callout--info conn-troubleshoot" data-testid="conn-troubleshoot">
                    <summary><strong>Trouble signing in?</strong> &nbsp;<span class="conn-dim">(open this if the popup didn't appear or the sign-in errored)</span></summary>
                    <ol class="conn-troubleshoot__list">
                      <li><strong>Make sure you're signed into Heist in this same browser first.</strong> The popup uses your existing Heist session — without one, the sign-in screen can render blank or get clipped. <a href="/login" target="_blank" rel="noopener" style="color:var(--primary);text-decoration:none;">Open Heist in a new tab →</a></li>
                      <li><strong>Allow popups from claude.ai or chatgpt.com.</strong> Most browsers show a blocked-popup icon in the address bar — click it and choose "Always allow."</li>
                      <li><strong>Try an incognito / private window if you have multiple Heist accounts.</strong> Shared cookies can route the popup to the wrong account.</li>
                      <li><strong>Still stuck?</strong> Email <a href="mailto:support@heistbrain.com?subject=Connector%20sign-in%20failed" style="color:var(--primary);text-decoration:none;">support@heistbrain.com</a> with a screenshot. If the error page shows a request ID, include it — that lets us grep the server log for the exact failure.</li>
                    </ol>
                  </details>
                </div>
              </article>
            </section>

            <section class="conn-section conn-advanced" data-open="${s.advancedOpen ? 'true' : 'false'}">
              <button class="conn-advanced__toggle" data-action="toggle-advanced" aria-expanded="${s.advancedOpen ? 'true' : 'false'}">
                <span class="conn-advanced__chev">${s.advancedOpen ? '▾' : '▸'}</span>
                <span class="conn-advanced__label">
                  <span class="conn-advanced__title">Advanced setup</span>
                  <span class="conn-advanced__sub">For Claude Desktop, Cursor, Windsurf, GitHub Copilot in VS Code, custom scripts, and API keys. Most users can ignore this.</span>
                </span>
              </button>

              ${s.advancedOpen ? `
                <div class="conn-advanced__body">
                  <article class="conn-card">
                    <header class="conn-card__head">
                      <div class="conn-card__badge conn-card__badge--muted">Desktop apps</div>
                      <h3>Claude Desktop, Cursor, or Windsurf</h3>
                      <p>These apps don't have an OAuth sign-in flow, so they need an API key and a config snippet. You'll need to open Terminal and edit one settings file.</p>
                    </header>

                    <div class="conn-callout conn-callout--info">
                      <div class="conn-callout__icon">ℹ</div>
                      <div>If you're only using Claude.ai or ChatGPT in the browser, you can skip everything below.</div>
                    </div>

                    <div class="conn-kv">
                      <span class="conn-kv__label">Step 1 — install the helper (run in Terminal)</span>
                      <div class="conn-copyline">
                        <code>${escapeHtml(stdioCmd)}</code>
                        <button class="conn-btn conn-btn--ghost" data-action="copy-text" data-copy="${escapeHtml(stdioCmd)}" data-label="Command copied">Copy</button>
                      </div>
                    </div>
                    <div class="conn-kv">
                      <span class="conn-kv__label">Step 2 — create a key (form below), then paste this into <code>claude_desktop_config.json</code></span>
                      <pre class="conn-code-block conn-code-block--lg"><button class="conn-btn conn-btn--ghost conn-code-block__copy" data-action="copy-text" data-copy="${escapeHtml(mcpConfigSnippet)}" data-label="Config copied">Copy</button>${escapeHtml(mcpConfigSnippet)}</pre>
                    </div>
                    <p class="conn-hint">Replace <code>hst_live_…</code> with a key from below, save, and restart the app. "heist" appears as a tool inside your chats when it's working.</p>
                  </article>

                  <article class="conn-card">
                    <header class="conn-card__head">
                      <div class="conn-card__badge conn-card__badge--muted">GitHub Copilot in VS Code</div>
                      <h3>VS Code (Copilot Chat with MCP)</h3>
                      <p>VS Code 1.99+ supports MCP servers natively in Copilot Chat — same idea as Claude Desktop, but the config schema is slightly different (<code>servers</code> with an explicit <code>type: "stdio"</code>). Lives in <code>.vscode/mcp.json</code> for a single workspace, or in your user settings for all workspaces.</p>
                    </header>

                    <div class="conn-callout conn-callout--info">
                      <div class="conn-callout__icon">ℹ</div>
                      <div>Requires VS Code 1.99 or newer with GitHub Copilot Chat installed and signed in.</div>
                    </div>

                    <div class="conn-kv">
                      <span class="conn-kv__label">Step 1 — install the helper (run in Terminal, same one Claude Desktop uses)</span>
                      <div class="conn-copyline">
                        <code>${escapeHtml(stdioCmd)}</code>
                        <button class="conn-btn conn-btn--ghost" data-action="copy-text" data-copy="${escapeHtml(stdioCmd)}" data-label="Command copied">Copy</button>
                      </div>
                    </div>
                    <div class="conn-kv">
                      <span class="conn-kv__label">Step 2 — create a key (form below), then paste this into <code>.vscode/mcp.json</code></span>
                      <pre class="conn-code-block conn-code-block--lg"><button class="conn-btn conn-btn--ghost conn-code-block__copy" data-action="copy-text" data-copy="${escapeHtml(copilotConfigSnippet)}" data-label="Copilot config copied">Copy</button>${escapeHtml(copilotConfigSnippet)}</pre>
                    </div>
                    <p class="conn-hint">Replace <code>hst_live_…</code> with a key from below, save, and reload the VS Code window. Open the chat (<code>⌃⌘I</code>) and pick <strong>Agent</strong> mode — Heist's tools appear in the tool picker.</p>
                    <p class="conn-hint">Also using Visual Studio 2022, GitHub Copilot CLI, or the Coding Agent? Same snippet, different file path — <a href="#help?tab=mcp#copilot-surfaces" target="_blank" rel="noopener" style="color:var(--primary);">see which Copilot surfaces work with MCP &rarr;</a></p>
                  </article>

                  <article class="conn-card">
                    <header class="conn-card__head">
                      <h3>Your API keys</h3>
                      <p>Only needed for the desktop apps above, for custom scripts, or for developer integrations. Make a separate key per app so you can revoke one without affecting the others.</p>
                    </header>
                    ${keysTableHtml}
                  </article>

                  <article class="conn-card">
                    <header class="conn-card__head">
                      <h3>Create a new key</h3>
                      <p>Give it a name so you remember what it's for, then pick what the AI is allowed to do.</p>
                    </header>
                    <div class="conn-form">
                      <label class="conn-field">
                        <span class="conn-field__label">Name this key</span>
                        <input id="key-name" type="text" class="conn-input" placeholder="e.g. Claude on my laptop" value="${escapeHtml(s.draft.name)}" autocomplete="off" />
                      </label>
                      <div class="conn-field">
                        <span class="conn-field__label">What is the AI allowed to do?</span>
                        <div class="conn-scopes">${scopeChips}</div>
                        <p class="conn-hint">
                          Hover any option to see what it does. <strong>Not sure?</strong> Leave <em>read</em> and <em>draft</em> turned on — the AI can write post drafts for you to review, without touching anything else. You can always add more later.
                        </p>
                      </div>
                      <div class="conn-actions">
                        <button class="conn-btn conn-btn--primary" data-action="create" ${s.creating ? 'disabled' : ''}>
                          ${s.creating ? 'Creating…' : 'Create key'}
                        </button>
                      </div>
                    </div>
                  </article>

                  <article class="conn-card conn-card--compact">
                    <div class="conn-linkrow">
                      <div>
                        <strong>Using Gemini or something else?</strong>
                        <div class="conn-dim">If your AI can't connect directly yet, you can still bring its drafts in: ask it for post drafts, copy what it wrote, and paste into the Agent Inbox.</div>
                      </div>
                      <a class="conn-btn conn-btn--ghost" href="#agent-inbox">Open Agent Inbox →</a>
                    </div>
                    <div class="conn-linkrow">
                      <div>
                        <strong>Developer / Custom GPT builder?</strong>
                        <div class="conn-dim">We publish an OpenAPI spec you can point a custom app at.</div>
                      </div>
                      <a class="conn-btn conn-btn--ghost" href="/api/openapi" target="_blank" rel="noopener">Developer docs →</a>
                    </div>
                  </article>
                </div>
              ` : ''}
            </section>
          </div>
          ${renderConnectorsStyles()}
        `;
    }
}

function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

function renderUpgradeWall() {
    return `
      <div class="conn-page">
        <div class="conn-upgrade">
          <div class="conn-upgrade__halo" aria-hidden="true"></div>
          <span class="conn-upgrade__eyebrow">Pro feature</span>
          <h2 class="conn-upgrade__title">Use Claude, ChatGPT, and Cursor as part of your Heist workflow.</h2>
          <p class="conn-upgrade__lede">
            Keep working in the AI chat you already love. Ask for posts, captions, or campaign ideas — and watch them appear in your Heist inbox, ready to edit and publish.
          </p>
          <ul class="conn-upgrade__list">
            <li><span class="conn-upgrade__bullet"></span> Turn any AI chat into your content co-writer</li>
            <li><span class="conn-upgrade__bullet"></span> The AI learns your brand, so posts sound like you</li>
            <li><span class="conn-upgrade__bullet"></span> Review and tweak every draft before it goes out</li>
            <li><span class="conn-upgrade__bullet"></span> Heist gets smarter every time you approve or reject a post</li>
          </ul>
          <a href="#upgrade" class="conn-btn conn-btn--primary conn-btn--lg">Upgrade to Pro</a>
          <p class="conn-upgrade__footnote">Included on Pro, Business, Founder, and Agency plans.</p>
        </div>
      </div>
      ${renderConnectorsStyles()}`;
}

function renderConnectorsStyles() {
    // All colors driven by theme tokens so light and dark mode stay on-brand.
    return `<style>
      .conn-page { max-width: 960px; margin: 0 auto; padding: 24px; display: flex; flex-direction: column; gap: 24px; }
      .conn-page code, .conn-page pre { font-family: var(--font-mono, 'JetBrains Mono', monospace); }

      /* ─── Hero ─── */
      .conn-hero {
        position: relative;
        padding: 36px 40px 40px;
        border-radius: var(--radius-modal, 16px);
        background: linear-gradient(135deg, var(--surface-1), var(--surface-2));
        border: 1px solid var(--border-subtle);
        overflow: hidden;
      }
      .conn-hero__halo {
        position: absolute; inset: -80px -80px auto auto;
        width: 320px; height: 320px; border-radius: 50%;
        background: radial-gradient(circle at center, rgba(139,92,246,0.35), transparent 65%);
        filter: blur(12px);
        pointer-events: none;
      }
      html.dark .conn-hero__halo { background: radial-gradient(circle at center, rgba(139,92,246,0.4), transparent 65%); }
      .conn-hero__inner { position: relative; max-width: 680px; }
      .conn-hero__eyebrow {
        display: inline-block;
        font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary);
        padding: 4px 10px; border-radius: 999px;
        background: var(--bg-accent-1); border: 1px solid rgba(139,92,246,0.25);
      }
      .conn-hero__title {
        /* Use the body font (DM Sans) instead of Instrument Serif — the
         * serif at 40px in dark mode reads thin/blurry. Match the page-
         * header weight + tracking the rest of the chrome uses. */
        margin: 16px 0 8px; font-family: var(--font-body, 'DM Sans', system-ui, sans-serif);
        font-size: clamp(24px, 2.6vw, 32px); line-height: 1.2;
        font-weight: 700; letter-spacing: -0.01em; color: var(--text);
      }
      .conn-hero__lede { margin: 0; font-size: 16px; line-height: 1.55; color: var(--text-2); }
      .conn-hero__lede a { color: var(--primary); text-decoration: none; border-bottom: 1px dashed var(--primary); }

      /* ─── Grid of two option cards ─── */
      .conn-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 18px;
      }
      @media (max-width: 820px) { .conn-grid { grid-template-columns: 1fr; } }

      /* ─── Cards ─── */
      .conn-card {
        position: relative; overflow: hidden;
        background: var(--surface-1);
        border: 1px solid var(--border-subtle);
        border-radius: var(--radius-card, 12px);
        box-shadow: var(--shadow-card, 0 1px 3px rgba(0,0,0,0.06), 0 4px 10px rgba(0,0,0,0.04));
        padding: 22px;
      }
      .conn-card--primary {
        border-color: rgba(245,158,11,0.35);
        box-shadow:
          0 1px 3px rgba(0,0,0,0.06),
          0 4px 14px rgba(245,158,11,0.12),
          0 0 0 1px rgba(245,158,11,0.12) inset;
      }
      html.dark .conn-card--primary {
        box-shadow:
          0 1px 3px rgba(0,0,0,0.4),
          0 8px 28px rgba(245,158,11,0.12),
          0 0 0 1px rgba(245,158,11,0.12) inset;
      }

      .conn-card__badge--muted { opacity: .75; }

      /* ─── Advanced collapsible section ─── */
      .conn-advanced {
        border-top: 1px dashed var(--border-subtle);
        padding-top: 18px;
      }
      .conn-advanced__toggle {
        display: flex; align-items: flex-start; gap: 14px;
        width: 100%; text-align: left;
        padding: 12px 14px;
        background: transparent;
        border: 1px solid var(--border-subtle);
        border-radius: 12px;
        color: var(--text-2);
        cursor: pointer; font-family: inherit;
        transition: all .18s ease;
      }
      .conn-advanced__toggle:hover {
        background: var(--surface-2); color: var(--text);
        border-color: var(--border);
      }
      .conn-advanced[data-open="true"] .conn-advanced__toggle {
        background: var(--surface-2); color: var(--text);
        border-color: var(--border);
        border-bottom-left-radius: 0; border-bottom-right-radius: 0;
        border-bottom-color: transparent;
      }
      .conn-advanced__chev {
        flex-shrink: 0; font-size: 14px; margin-top: 2px;
        color: var(--text-muted);
      }
      .conn-advanced[data-open="true"] .conn-advanced__chev { color: var(--primary); }
      .conn-advanced__label { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
      .conn-advanced__title {
        font-family: var(--font-display); font-size: 15px; font-weight: 600;
        color: var(--text); letter-spacing: var(--tracking-tight);
      }
      .conn-advanced__sub { font-size: 12.5px; line-height: 1.45; color: var(--text-muted); }
      .conn-advanced__body {
        display: flex; flex-direction: column; gap: 16px;
        padding: 18px;
        border: 1px solid var(--border-subtle);
        border-top: 0;
        border-radius: 0 0 12px 12px;
        background: linear-gradient(180deg, var(--surface-2) 0%, transparent 40%);
      }

      /* Walkthrough block on the primary card */
      .conn-walkthrough { margin: 16px 0 10px; }
      .conn-walkthrough__title {
        font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--primary); margin: 16px 0 8px;
      }
      .conn-walkthrough__title:first-child { margin-top: 0; }
      .conn-walkthrough__text {
        margin: 0 0 12px; color: var(--text-2); font-size: 13.5px; line-height: 1.55;
      }
      .conn-walkthrough__text em { font-style: normal; color: var(--text); font-weight: 500; }
      .conn-walkthrough__text strong { color: var(--text); }

      /* Fake-field rendering: mirrors the real dialog's label + input look */
      .conn-fake-field {
        margin: 10px 0;
        border: 1px solid var(--border-subtle);
        border-radius: 10px;
        background: var(--surface-2);
        overflow: hidden;
      }
      .conn-fake-field__label {
        display: flex; justify-content: space-between; align-items: center;
        padding: 7px 12px 6px; font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--text-muted);
        background: rgba(255,255,255,0.02);
        border-bottom: 1px solid var(--border-subtle);
      }
      html:not(.dark) .conn-fake-field__label { background: rgba(0,0,0,0.02); }
      .conn-fake-field__must {
        font-size: 9.5px; color: #F59E0B;
        text-transform: uppercase; letter-spacing: 0.1em;
        padding: 1px 6px; border-radius: 999px;
        background: rgba(245,158,11,0.12); border: 1px solid rgba(245,158,11,0.28);
      }
      .conn-fake-field__value {
        display: flex; gap: 10px; align-items: center;
        padding: 11px 12px;
      }
      .conn-fake-field__value code {
        flex: 1; font-size: 13px; color: var(--text);
        word-break: break-all;
      }
      /* Prompt variant — multi-line, tinted purple to read as a separate
       * affordance from the literal Name/URL fields above it. */
      .conn-fake-field--prompt {
        border-color: rgba(139,92,246,0.32);
        background: rgba(139,92,246,0.06);
      }
      .conn-fake-field--prompt .conn-fake-field__label {
        background: rgba(139,92,246,0.08);
        border-bottom-color: rgba(139,92,246,0.22);
        color: var(--primary, #A78BFA);
      }
      .conn-fake-field__value--block {
        flex-direction: column; align-items: stretch; gap: 12px;
      }
      .conn-fake-field__value--block code {
        white-space: pre-wrap; word-break: break-word;
        font-family: var(--font-mono, 'JetBrains Mono', monospace);
        font-size: 12.5px; line-height: 1.55;
        background: rgba(0,0,0,0.18); padding: 10px 12px; border-radius: 6px;
        border: 1px solid var(--border-subtle, rgba(255,255,255,0.06));
      }
      .conn-fake-field__value--block .conn-btn { align-self: flex-end; }
      .conn-btn--sm {
        padding: 5px 10px; font-size: 11.5px; border-radius: 7px;
      }

      /* Callouts (warn / info) */
      .conn-callout {
        display: flex; gap: 12px; align-items: flex-start;
        padding: 12px 14px; margin: 14px 0;
        border-radius: 10px;
        font-size: 13px; line-height: 1.55;
      }
      .conn-callout__icon {
        flex-shrink: 0;
        width: 24px; height: 24px; display: grid; place-items: center;
        border-radius: 8px; font-size: 14px;
      }
      .conn-callout--warn {
        background: linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04));
        border: 1px solid rgba(245,158,11,0.35);
        color: var(--text);
      }
      .conn-callout--warn .conn-callout__icon {
        background: rgba(245,158,11,0.22);
        border: 1px solid rgba(245,158,11,0.4);
        color: #F59E0B;
      }
      .conn-callout--warn strong { color: #F59E0B; }
      .conn-callout--info {
        background: linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.02));
        border: 1px solid rgba(59,130,246,0.28);
        color: var(--text-2);
      }
      .conn-callout--info .conn-callout__icon {
        background: rgba(59,130,246,0.18);
        border: 1px solid rgba(59,130,246,0.35);
        color: #60A5FA;
      }

      /* Modal intro line */
      .conn-modal__where-intro {
        font-size: 13px; line-height: 1.55; color: var(--text-2);
        margin: 0 0 12px;
      }
      .conn-modal__where-intro strong { color: var(--text); }
      html.dark .conn-card { box-shadow: 0 1px 3px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04); }
      .conn-card--option { padding-left: 28px; }
      .conn-card--compact { padding: 8px 4px; }
      .conn-card__rail {
        position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
        background: var(--rail, var(--gradient-primary));
      }
      .conn-card__head { margin-bottom: 16px; }
      .conn-card__head h3 {
        /* Match the chrome font system used elsewhere (DM Sans 700) so
         * card titles read consistent with the page header above. The
         * earlier Instrument Serif rendered thin in dark mode. */
        margin: 8px 0 6px;
        font-family: var(--font-body, 'DM Sans', system-ui, sans-serif);
        font-size: 20px; font-weight: 700; letter-spacing: -0.01em;
        color: var(--text); line-height: 1.25;
      }
      .conn-card__head p { margin: 0; color: var(--text-2); font-size: 14px; line-height: 1.5; }
      .conn-card__badge {
        display: inline-block; font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.1em; text-transform: uppercase;
        color: var(--text-2); padding: 3px 8px; border-radius: 999px;
        background: var(--surface-2); border: 1px solid var(--border-subtle);
      }

      /* ─── Key/Value copy rows ─── */
      .conn-kv { margin: 14px 0; }
      .conn-kv__label {
        display: block; font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-muted);
        margin-bottom: 6px;
      }
      .conn-copyline {
        display: flex; align-items: center; gap: 8px;
        background: var(--surface-2); border: 1px solid var(--border-subtle);
        border-radius: 10px; padding: 8px 10px;
      }
      .conn-copyline code {
        flex: 1; font-size: 13px; color: var(--text); word-break: break-all;
        background: transparent; border: 0;
      }

      /* ─── Steps ─── */
      .conn-steps { margin: 14px 0 0; padding: 0; list-style: none; counter-reset: step; }
      .conn-steps li {
        position: relative; padding: 6px 0 6px 34px; color: var(--text-2); font-size: 14px;
        counter-increment: step; line-height: 1.45;
      }
      .conn-steps li::before {
        content: counter(step);
        position: absolute; left: 0; top: 6px;
        width: 22px; height: 22px; border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
        font-family: var(--font-mono); font-size: 11px; font-weight: 600;
        color: var(--primary);
        background: var(--bg-accent-1);
        border: 1px solid rgba(139,92,246,0.25);
      }
      .conn-steps li em { font-style: normal; color: var(--text); font-weight: 500; }

      /* ─── Section headings ─── */
      .conn-section__head {
        display: flex; flex-direction: column; gap: 4px;
        margin-bottom: 12px;
      }
      .conn-section__head h2 {
        margin: 0; font-family: var(--font-display); font-size: 26px;
        letter-spacing: var(--tracking-tight); color: var(--text);
        line-height: 1.15;
      }
      .conn-section__head .conn-dim { margin: 0; color: var(--text-2); font-size: 14px; }

      /* ─── Form ─── */
      .conn-form { display: flex; flex-direction: column; gap: 18px; }
      .conn-field { display: block; }
      .conn-field__label {
        display: block; font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-muted);
        margin-bottom: 8px;
      }
      .conn-input {
        width: 100%; padding: 11px 13px; font-size: 14px;
        color: var(--text); background: var(--surface-2);
        border: 1px solid var(--border); border-radius: 10px;
        transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        font-family: inherit;
      }
      .conn-input::placeholder { color: var(--text-muted); }
      .conn-input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px var(--focus-ring);
      }
      .conn-hint { margin: 10px 0 0; color: var(--text-muted); font-size: 12.5px; line-height: 1.5; }
      .conn-hint strong { color: var(--text-2); }
      .conn-hint em, .conn-hint code { font-style: normal; color: var(--text); background: var(--surface-2); padding: 1px 6px; border-radius: 4px; font-family: var(--font-mono); font-size: 11.5px; }
      .conn-actions { display: flex; gap: 10px; justify-content: flex-end; }

      /* ─── Scopes ─── */
      .conn-scopes { display: flex; flex-wrap: wrap; gap: 8px; }
      .conn-scope {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 6px 11px; font-size: 12px; font-family: var(--font-mono);
        color: var(--text-2);
        background: var(--surface-2); border: 1px solid var(--border-subtle);
        border-radius: 999px; cursor: pointer;
        transition: all .15s ease;
      }
      .conn-scope:hover { border-color: var(--border); color: var(--text); }
      .conn-scope__dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: var(--dot, var(--primary));
        box-shadow: 0 0 8px var(--dot, var(--primary));
        flex-shrink: 0;
      }
      .conn-scope.is-selected {
        background: linear-gradient(135deg, rgba(139,92,246,0.16), rgba(109,40,217,0.08));
        border-color: rgba(139,92,246,0.45);
        color: var(--text);
        box-shadow: 0 0 0 1px rgba(139,92,246,0.2) inset;
      }
      html.dark .conn-scope.is-selected {
        background: linear-gradient(135deg, rgba(139,92,246,0.22), rgba(109,40,217,0.12));
      }
      .conn-scope--mini { padding: 3px 8px; font-size: 10.5px; cursor: default; }
      .conn-scope--mini .conn-scope__dot { width: 6px; height: 6px; }

      /* ─── Buttons ─── */
      .conn-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 6px;
        padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600;
        font-family: inherit; cursor: pointer;
        border: 1px solid var(--border); color: var(--text);
        background: var(--surface-2);
        transition: all .15s ease;
        white-space: nowrap;
      }
      .conn-btn:hover { background: var(--surface-3); border-color: var(--border-strong); }
      .conn-btn--primary {
        color: #fff;
        background: linear-gradient(180deg, #9B7BF2 0%, #7C5CE0 55%, #6D49D4 100%);
        border: 1px solid rgba(139,92,246,0.55);
        box-shadow:
          0 1px 0 rgba(255,255,255,0.18) inset,
          0 -1px 0 rgba(0,0,0,0.15) inset,
          0 2px 6px rgba(109,40,217,0.25),
          0 4px 14px rgba(109,40,217,0.18);
        letter-spacing: 0.01em;
        transition: transform .15s ease, box-shadow .18s ease, filter .18s ease;
      }
      .conn-btn--primary:hover {
        filter: brightness(1.06);
        box-shadow:
          0 1px 0 rgba(255,255,255,0.22) inset,
          0 -1px 0 rgba(0,0,0,0.18) inset,
          0 3px 10px rgba(109,40,217,0.35),
          0 6px 22px rgba(109,40,217,0.28);
        transform: translateY(-1px);
      }
      .conn-btn--primary:active { transform: translateY(0); filter: brightness(0.98); }
      .conn-btn.is-copied {
        color: #fff !important;
        background: linear-gradient(180deg, #34D399 0%, #10B981 55%, #059669 100%) !important;
        border: 1px solid rgba(16,185,129,0.55) !important;
        box-shadow:
          0 1px 0 rgba(255,255,255,0.22) inset,
          0 -1px 0 rgba(0,0,0,0.15) inset,
          0 2px 6px rgba(16,185,129,0.3),
          0 6px 22px rgba(16,185,129,0.22) !important;
        animation: conn-copied-bump .32s cubic-bezier(.2,.9,.3,1.4);
      }
      .conn-btn.is-copied svg { stroke: #fff; }
      @keyframes conn-copied-bump {
        0%   { transform: scale(.96); }
        55%  { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      /* Floating "Copied!" pill above the button */
      .conn-copied-pill {
        position: absolute;
        left: 50%; top: -8px;
        transform: translate(-50%, 0);
        padding: 4px 10px;
        border-radius: 999px;
        font-family: var(--font-mono); font-size: 10.5px;
        font-weight: 600; letter-spacing: 0.06em;
        color: #fff;
        background: linear-gradient(180deg, #34D399, #059669);
        border: 1px solid rgba(16,185,129,0.6);
        box-shadow: 0 4px 14px rgba(16,185,129,0.4), 0 1px 0 rgba(255,255,255,0.2) inset;
        white-space: nowrap; pointer-events: none;
        animation: conn-copied-pill .9s ease-out forwards;
      }
      .conn-copied-pill::after {
        content: ''; position: absolute; left: 50%; bottom: -4px;
        transform: translateX(-50%) rotate(45deg);
        width: 7px; height: 7px;
        background: #10B981;
        border-right: 1px solid rgba(16,185,129,0.6);
        border-bottom: 1px solid rgba(16,185,129,0.6);
      }
      @keyframes conn-copied-pill {
        0%   { opacity: 0; transform: translate(-50%, 4px) scale(.85); }
        18%  { opacity: 1; transform: translate(-50%, -28px) scale(1); }
        78%  { opacity: 1; transform: translate(-50%, -28px) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -38px) scale(.95); }
      }
      .conn-btn--ghost {
        background: transparent; border-color: var(--border-subtle);
        color: var(--text-2); font-weight: 500;
      }
      .conn-btn--ghost:hover { background: var(--surface-2); color: var(--text); }
      .conn-btn--danger-ghost {
        background: transparent; border-color: rgba(239,68,68,0.3);
        color: var(--danger); font-size: 12px; padding: 5px 10px;
      }
      .conn-btn--danger-ghost:hover { background: var(--danger-bg); border-color: var(--danger); }
      .conn-btn--lg { padding: 13px 26px; font-size: 14px; }
      .conn-btn:disabled { opacity: .5; cursor: not-allowed; }

      /* ─── Code blocks ─── */
      .conn-code-inline {
        padding: 2px 7px; font-size: 12px;
        color: var(--text); background: var(--surface-2);
        border: 1px solid var(--border-subtle); border-radius: 5px;
      }
      .conn-code-block {
        display: block; padding: 14px 16px; font-size: 12.5px; line-height: 1.55;
        color: var(--text); background: var(--surface-0);
        border: 1px solid var(--border-subtle); border-radius: 10px;
        white-space: pre-wrap; word-break: break-all; overflow-x: auto; margin: 0;
        font-family: var(--font-mono);
      }
      html.dark .conn-code-block { background: #030209; border-color: rgba(255,255,255,0.06); }
      .conn-code-block--lg { position: relative; padding-top: 40px; font-size: 12px; }
      .conn-code-block__copy {
        position: absolute; top: 8px; right: 8px; padding: 4px 10px; font-size: 11px;
      }
      pre.conn-code-block { font-size: 12px; }

      /* ─── Keys table ─── */
      .conn-table-wrap { overflow-x: auto; margin: -8px; padding: 8px; }
      .conn-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      .conn-table th {
        text-align: left; padding: 10px 12px; font-family: var(--font-mono);
        font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase;
        color: var(--text-muted); font-weight: 500;
        border-bottom: 1px solid var(--border-subtle);
      }
      .conn-table td {
        padding: 14px 12px; vertical-align: middle;
        border-bottom: 1px solid var(--border-subtle);
        color: var(--text-2);
      }
      .conn-table tr:last-child td { border-bottom: 0; }
      .conn-table tr:hover td { background: var(--surface-2); }
      .conn-table tr.is-revoked td { opacity: .55; }
      .conn-table__name { color: var(--text); font-weight: 600; }
      .conn-table__sub { color: var(--text-muted); font-size: 11.5px; font-family: var(--font-mono); margin-top: 2px; }
      .conn-dim { color: var(--text-muted); }

      .conn-status { font-size: 11px; font-family: var(--font-mono); letter-spacing: 0.05em; }
      .conn-status--active { color: var(--success); }
      .conn-status--revoked { color: var(--text-muted); }

      /* ─── Empty / skeleton ─── */
      .conn-empty {
        text-align: center; padding: 40px 20px; border-radius: 10px;
        background: var(--surface-2); border: 1px dashed var(--border-subtle);
      }
      .conn-empty__title { font-family: var(--font-display); font-size: 20px; color: var(--text); }
      .conn-empty__sub { margin-top: 6px; color: var(--text-2); font-size: 14px; }
      .conn-empty--error { color: var(--danger); border-color: rgba(239,68,68,0.3); background: var(--danger-bg); }
      .conn-skeleton {
        height: 42px; margin: 8px 0; border-radius: 8px;
        background: linear-gradient(90deg, var(--surface-2), var(--surface-3), var(--surface-2));
        background-size: 200% 100%;
        animation: conn-shimmer 1.4s ease-in-out infinite;
      }
      @keyframes conn-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      /* ─── Link rows ─── */
      .conn-linkrow {
        display: flex; align-items: center; justify-content: space-between;
        gap: 16px; padding: 16px 22px;
        border-bottom: 1px solid var(--border-subtle);
      }
      .conn-linkrow:last-child { border-bottom: 0; }
      .conn-linkrow strong { color: var(--text); }
      .conn-linkrow .conn-dim { font-size: 12.5px; margin-top: 2px; }

      /* ─── Modal (new key shown once) ─── */
      .conn-modal-overlay {
        position: fixed; inset: 0; z-index: 1000;
        background: radial-gradient(ellipse at center, rgba(15,10,35,0.78), rgba(3,2,9,0.88));
        backdrop-filter: blur(10px) saturate(140%);
        -webkit-backdrop-filter: blur(10px) saturate(140%);
        display: grid; place-items: center;
        padding: 24px;
        animation: conn-fade-in .22s ease-out;
      }
      @keyframes conn-fade-in { from { opacity: 0 } to { opacity: 1 } }
      .conn-modal {
        position: relative;
        width: 100%; max-width: 580px;
        background:
          radial-gradient(circle at top right, rgba(245,158,11,0.10), transparent 50%),
          linear-gradient(180deg, var(--surface-1), var(--surface-2));
        border: 1px solid var(--border-subtle);
        border-radius: 20px;
        box-shadow:
          0 1px 0 rgba(255,255,255,0.04) inset,
          0 24px 80px rgba(0,0,0,0.55),
          0 8px 24px rgba(0,0,0,0.3);
        padding: 32px;
        animation: conn-modal-in .28s cubic-bezier(.2,.9,.3,1.15);
      }
      .conn-modal::before {
        content: ''; position: absolute; inset: -1px;
        border-radius: 20px; padding: 1px; pointer-events: none;
        background: linear-gradient(135deg, rgba(245,158,11,0.35), rgba(139,92,246,0.2) 40%, transparent 70%);
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor; mask-composite: exclude;
      }
      @keyframes conn-modal-in {
        from { opacity: 0; transform: translateY(16px) scale(.96) }
        to   { opacity: 1; transform: translateY(0) scale(1) }
      }
      .conn-modal__head { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 24px; }
      .conn-modal__icon {
        font-size: 22px; line-height: 1;
        width: 48px; height: 48px; display: grid; place-items: center;
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(245,158,11,0.22), rgba(217,119,6,0.08));
        border: 1px solid rgba(245,158,11,0.25);
        box-shadow: 0 4px 14px rgba(245,158,11,0.18), 0 1px 0 rgba(255,255,255,0.06) inset;
        flex-shrink: 0;
      }
      .conn-modal__title {
        font-family: var(--font-display); font-size: 24px; color: var(--text);
        line-height: 1.15; letter-spacing: var(--tracking-tight); font-weight: 600;
      }
      .conn-modal__sub { color: var(--text-2); font-size: 13.5px; margin-top: 6px; line-height: 1.6; }
      .conn-modal__sub strong { color: #F5B841; font-weight: 600; }
      .conn-modal__keywrap { margin: 18px 0; }
      .conn-modal__keylabel {
        font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--text-muted); margin-bottom: 8px;
      }
      .conn-modal__keyrow { display: flex; gap: 10px; align-items: stretch; }
      .conn-modal__keyrow .conn-code-block {
        flex: 1; font-size: 12px; margin: 0;
        display: flex; align-items: center;
        background: rgba(3,2,9,0.6); border-color: rgba(245,158,11,0.2);
      }
      html:not(.dark) .conn-modal__keyrow .conn-code-block { background: rgba(0,0,0,0.04); }
      .conn-modal__meta {
        display: flex; flex-direction: column; gap: 10px;
        padding: 14px 16px; margin: 0 0 22px;
        background: rgba(255,255,255,0.02);
        border: 1px solid var(--border-subtle);
        border-radius: 12px;
      }
      html:not(.dark) .conn-modal__meta { background: rgba(0,0,0,0.02); }
      .conn-modal__meta-row {
        display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
        font-size: 13px; color: var(--text);
      }
      .conn-modal__meta-label {
        font-family: var(--font-mono); font-size: 10.5px;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--text-muted); min-width: 64px;
      }
      .conn-modal__scopes { display: inline-flex; flex-wrap: wrap; gap: 6px; }

      /* ─── "Where to paste" block ─── */
      .conn-modal__where {
        margin: 0 0 24px;
        padding: 16px 18px;
        background: linear-gradient(135deg, rgba(139,92,246,0.08), rgba(139,92,246,0.02));
        border: 1px solid rgba(139,92,246,0.22);
        border-radius: 12px;
      }
      .conn-modal__where-title {
        font-family: var(--font-display); font-size: 14px;
        color: var(--text); font-weight: 600; margin-bottom: 10px;
        letter-spacing: var(--tracking-tight);
        display: flex; align-items: center; gap: 8px;
      }
      .conn-modal__where-title::before {
        content: ''; width: 6px; height: 6px; border-radius: 50%;
        background: var(--primary);
        box-shadow: 0 0 10px rgba(139,92,246,0.6);
      }
      .conn-modal__where-steps {
        margin: 0; padding: 0 0 0 0; list-style: none;
        counter-reset: wstep;
        display: flex; flex-direction: column; gap: 10px;
      }
      .conn-modal__where-steps li {
        position: relative; padding-left: 28px;
        font-size: 12.5px; line-height: 1.55; color: var(--text-2);
        counter-increment: wstep;
      }
      .conn-modal__where-steps li::before {
        content: counter(wstep);
        position: absolute; left: 0; top: 1px;
        width: 18px; height: 18px; border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
        font-family: var(--font-mono); font-size: 10px; font-weight: 600;
        color: var(--primary);
        background: rgba(139,92,246,0.14);
        border: 1px solid rgba(139,92,246,0.3);
      }
      .conn-modal__where-steps li strong { color: var(--text); font-weight: 600; }
      .conn-modal__where-steps li code {
        font-size: 11px; padding: 1px 6px; border-radius: 4px;
        background: rgba(139,92,246,0.1); color: var(--text);
        border: 1px solid rgba(139,92,246,0.18);
      }

      .conn-modal__actions { display: flex; justify-content: flex-end; gap: 10px; }
      .conn-modal__actions .conn-btn--primary { min-width: 170px; }

      /* ─── Refined primary / copy buttons — softer, more premium ─── */
      .conn-btn--copy {
        color: var(--text);
        background:
          linear-gradient(180deg, rgba(139,92,246,0.18), rgba(109,40,217,0.08));
        border: 1px solid rgba(139,92,246,0.3);
        box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 2px 8px rgba(109,40,217,0.12);
        transition: all .18s ease;
      }
      .conn-btn--copy:hover {
        background: linear-gradient(180deg, rgba(139,92,246,0.28), rgba(109,40,217,0.14));
        border-color: rgba(139,92,246,0.45);
        box-shadow: 0 1px 0 rgba(255,255,255,0.08) inset, 0 4px 12px rgba(109,40,217,0.2);
      }

      /* ─── Revoked keys toggle ─── */
      .conn-revoked-bar {
        display: flex; justify-content: center;
        padding: 14px 0 4px; border-top: 1px dashed var(--border-subtle); margin-top: 8px;
      }
      .conn-revoked-toggle {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 7px 14px; border-radius: 999px;
        background: transparent; border: 1px solid var(--border-subtle);
        color: var(--text-muted); font-size: 12px; font-family: var(--font-mono);
        cursor: pointer; transition: all .15s ease;
      }
      .conn-revoked-toggle:hover { color: var(--text-2); border-color: var(--border); background: var(--surface-2); }
      .conn-revoked-toggle__chev { font-size: 11px; }
      .conn-revoked-toggle__count {
        display: inline-grid; place-items: center;
        min-width: 20px; height: 20px; padding: 0 6px;
        border-radius: 10px; background: var(--surface-2);
        border: 1px solid var(--border-subtle);
        font-size: 11px; color: var(--text-muted);
      }

      /* ─── Upgrade wall ─── */
      .conn-upgrade {
        position: relative; overflow: hidden; max-width: 680px; margin: 0 auto;
        padding: 56px 40px 48px;
        background: linear-gradient(135deg, var(--surface-1), var(--surface-2));
        border: 1px solid var(--border-subtle);
        border-radius: var(--radius-modal);
        text-align: center;
        box-shadow: var(--shadow-elevation);
      }
      .conn-upgrade__halo {
        position: absolute; top: -120px; left: 50%; transform: translateX(-50%);
        width: 560px; height: 280px; border-radius: 50%;
        background: radial-gradient(circle at center, rgba(139,92,246,0.28), transparent 60%);
        filter: blur(14px); pointer-events: none;
      }
      html.dark .conn-upgrade__halo { background: radial-gradient(circle at center, rgba(139,92,246,0.35), transparent 60%); }
      .conn-upgrade > * { position: relative; }
      .conn-upgrade__eyebrow {
        display: inline-block; font-family: var(--font-mono); font-size: 11px;
        letter-spacing: 0.14em; text-transform: uppercase; color: var(--primary);
        padding: 5px 12px; border-radius: 999px;
        background: var(--bg-accent-1); border: 1px solid rgba(139,92,246,0.3);
      }
      .conn-upgrade__title {
        margin: 20px 0 10px; font-family: var(--font-display);
        font-size: clamp(30px, 3.4vw, 42px); line-height: 1.1;
        letter-spacing: var(--tracking-tight); color: var(--text);
      }
      .conn-upgrade__lede {
        max-width: 520px; margin: 0 auto 28px; color: var(--text-2);
        font-size: 15.5px; line-height: 1.6;
      }
      .conn-upgrade__list { max-width: 480px; margin: 0 auto 30px; padding: 0; list-style: none; text-align: left; }
      .conn-upgrade__list li { display: flex; gap: 12px; padding: 6px 0; color: var(--text-2); font-size: 14.5px; }
      .conn-upgrade__bullet {
        width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0; margin-top: 2px;
        background: var(--gradient-primary); position: relative;
        box-shadow: 0 0 10px rgba(139,92,246,0.4);
      }
      .conn-upgrade__bullet::after {
        content: '✓'; position: absolute; inset: 0;
        display: grid; place-items: center; color: #fff; font-size: 11px; font-weight: 700;
      }
      .conn-upgrade__footnote {
        margin: 18px 0 0; color: var(--text-muted); font-size: 12.5px;
      }
    </style>`;
}
